import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

export type ProposalSummaryRow = {
  key?: string;
  label: string;
  amount: string;
  indent?: boolean;
  muted?: boolean;
  strong?: boolean;
  emphasis?: boolean;
  dividerBefore?: boolean;
  helperText?: string;
};

type Props = {
  title?: string;
  rows: ProposalSummaryRow[];
  variant?: "gradient" | "plain";
  className?: string;
};

export default function ProposalCommercialSummaryCard({
  title = "Proposal summary",
  rows,
  variant = "gradient",
  className,
}: Props) {
  return (
    <Card
      className={cn(
        variant === "gradient" ? "border-[#1A2744]/12 bg-gradient-to-br from-[#FAFAF7] to-white shadow-sm" : "min-w-0 overflow-hidden",
        className,
      )}
    >
      {title ? (
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold" style={{ color: PROPOSAL_TEXT }}>
            {title}
          </CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className={cn("space-y-4 text-sm", !title && "p-4")} style={{ fontFamily: PROPOSAL_COPY_FONT }}>
        {rows.map((row, idx) => (
          <div
            key={row.key ?? `${row.label}-${idx}`}
            className={cn(row.dividerBefore && "border-t border-[#1A2744]/10 pt-3")}
          >
            <div
              className={cn(
                "flex items-baseline justify-between gap-4",
                row.indent && "pl-3 text-xs",
              )}
            >
              <span
                className={cn(
                  row.emphasis
                    ? "text-base font-semibold text-[#1A2744]"
                    : row.strong
                      ? "text-base font-semibold text-[#1A2744]"
                      : row.muted
                        ? "text-[#1A2744]/60"
                        : "text-[#1A2744]/75",
                  !row.muted && !row.strong && !row.emphasis && row.indent && "text-[#1A2744]/60",
                  row.strong && !row.emphasis && !row.indent && "font-medium text-[#1A2744]",
                )}
              >
                {row.label}
              </span>
              {row.amount ? (
                <span
                  className={cn(
                    "tabular-nums",
                    row.emphasis
                      ? "text-xl font-bold text-[#1A2744] sm:text-2xl"
                      : row.strong
                        ? "text-lg font-semibold text-[#1A2744]"
                        : "font-medium text-[#1A2744]",
                    row.muted && "text-[#1A2744]/75",
                  )}
                >
                  {row.amount}
                </span>
              ) : null}
            </div>
            {row.helperText ? (
              <p className={cn("mt-1 text-xs text-[#1A2744]/65", row.indent && "pl-3")}>{row.helperText}</p>
            ) : null}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** Retail footer: large total + optional breakdown block (non-card). */
export function ProposalRetailItemsFooter({
  totalLabel,
  totalFormatted,
  breakdown,
  notes,
}: {
  totalLabel?: string;
  totalFormatted: string;
  breakdown?: ReactNode;
  notes?: string | null;
}) {
  return (
    <>
      <div className="flex justify-end border-t border-[#1A2744]/15 pt-2">
        <p className="text-xl font-semibold">
          {totalLabel ?? "TOTAL"} {totalFormatted}
        </p>
      </div>
      {breakdown}
      {notes?.trim() ? (
        <div className="rounded-md border border-[#1A2744]/10 bg-white/55 p-3">
          <p className="mb-1 text-xs uppercase tracking-wide opacity-60">Notes on this proposal</p>
          <p className="whitespace-pre-wrap text-sm">{notes}</p>
        </div>
      ) : null}
    </>
  );
}
